/**
 * Single source of truth for domain, brand and string normalisation used by
 * every head tag, JSON-LD node and build script.
 */

export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://glowkit.studio").replace(
  /\/+$/,
  "",
);
export const SITE_NAME = "Glowkit";
export const SITE_LOCALE = "it_IT";
export const SITE_LANGUAGE = "it-IT";

/** Editorial vanity still-life from the seed image set, cropped to the OG ratio. */
export const DEFAULT_OG_IMAGE =
  "https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=1200&h=630&fit=crop&auto=format&q=85";

export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 155;

const BRAND_SUFFIX = ` — ${SITE_NAME}`;
const IMAGE_CDN_HOSTS = ["images.unsplash.com", "images.pexels.com"];

/**
 * Turns a relative path into an absolute canonical URL.
 * Collapses duplicate slashes and drops the trailing slash on every path but the root.
 *
 * @param {string} [path] Relative path, with or without a leading slash.
 * @returns {string} Absolute URL on SITE_URL.
 */
export function absoluteUrl(path = "/") {
  const raw = String(path || "/").trim();
  if (/^https?:\/\//i.test(raw)) return raw;

  const withLeadingSlash = raw.startsWith("/") ? raw : `/${raw}`;
  const collapsed = withLeadingSlash.replace(/\/{2,}/g, "/");
  if (collapsed === "/") return `${SITE_URL}/`;

  return `${SITE_URL}${collapsed.replace(/\/+$/, "")}`;
}

/**
 * Normalises an image reference into an absolute Open Graph image URL.
 * Known photo CDNs are re-cropped to 1200x630; anything else is passed through.
 *
 * @param {string} [src] Absolute image URL, site-relative path, or nothing.
 * @returns {string} Absolute image URL suitable for og:image.
 */
export function ogImageUrl(src) {
  const raw = String(src || "").trim();
  if (!raw) return DEFAULT_OG_IMAGE;

  if (!/^https?:\/\//i.test(raw)) {
    return raw.startsWith("/") ? absoluteUrl(raw) : DEFAULT_OG_IMAGE;
  }

  let parsed;
  try {
    parsed = new URL(raw);
  } catch {
    return DEFAULT_OG_IMAGE;
  }

  if (!IMAGE_CDN_HOSTS.includes(parsed.hostname)) return parsed.toString();

  parsed.searchParams.set("w", String(OG_IMAGE_WIDTH));
  parsed.searchParams.set("h", String(OG_IMAGE_HEIGHT));
  parsed.searchParams.set("fit", "crop");
  parsed.searchParams.set("auto", "format");
  return parsed.toString();
}

/**
 * Truncates on a word boundary without leaving dangling punctuation.
 *
 * @param {string} text
 * @param {number} max
 * @returns {string}
 */
function truncateOnWord(text, max) {
  const normalised = String(text || "").replace(/\s+/g, " ").trim();
  if (normalised.length <= max) return normalised;

  const hardCut = normalised.slice(0, max);
  const lastSpace = hardCut.lastIndexOf(" ");
  const cut = lastSpace > max * 0.5 ? hardCut.slice(0, lastSpace) : hardCut;
  return cut.replace(/[\s,;:.\-–—]+$/, "");
}

/**
 * Appends the brand suffix, truncating the page-specific part so the brand always survives.
 *
 * @param {string} title Page title without brand suffix.
 * @returns {string} Title of at most TITLE_MAX characters.
 */
export function formatTitle(title) {
  const specific = String(title || "").replace(/\s+/g, " ").trim();
  if (!specific) return SITE_NAME;
  if (specific === SITE_NAME || specific.endsWith(BRAND_SUFFIX)) {
    return truncateOnWord(specific, TITLE_MAX);
  }

  const budget = TITLE_MAX - BRAND_SUFFIX.length;
  if (budget <= 0) return SITE_NAME;
  return `${truncateOnWord(specific, budget)}${BRAND_SUFFIX}`;
}

/** A truncation shorter than this fraction of the limit wastes too much of the snippet. */
const SENTENCE_CUT_RATIO = 0.6;

/**
 * Clamps a meta description to DESCRIPTION_MAX.
 * Prefers the last complete sentence that fits, so a clamped description never
 * ends on a dangling word; falls back to a word boundary.
 *
 * @param {string} text
 * @returns {string}
 */
export function clampDescription(text) {
  const clamped = truncateOnWord(text, DESCRIPTION_MAX);
  const normalised = String(text || "").replace(/\s+/g, " ").trim();
  if (clamped === normalised) return clamped;

  const lastSentenceEnd = Math.max(
    clamped.lastIndexOf("."),
    clamped.lastIndexOf("!"),
    clamped.lastIndexOf("?"),
  );
  if (lastSentenceEnd >= DESCRIPTION_MAX * SENTENCE_CUT_RATIO) {
    return clamped.slice(0, lastSentenceEnd + 1);
  }
  return clamped;
}

const ITALIAN_MONTHS = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
];

function pad(value) {
  return String(value).padStart(2, "0");
}

/**
 * Parses a publication date into an ISO calendar date.
 * Accepts a Date, an ISO string, or an Italian long date such as "12 dicembre 2025".
 * A month/year pair without a day is *not* a date: it returns null rather than
 * guessing a day, because publication dates must never be fabricated.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string|null} "YYYY-MM-DD", or null when no real date can be derived.
 */
export function toIsoDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, year, month, day] = iso;
    if (Number(month) >= 1 && Number(month) <= 12 && Number(day) >= 1 && Number(day) <= 31) {
      return `${year}-${month}-${day}`;
    }
    return null;
  }

  const italian = raw.match(/^(\d{1,2})\s+([A-Za-zÀ-ÿ]+)\s+(\d{4})$/);
  if (italian) {
    const [, day, monthName, year] = italian;
    const monthIndex = ITALIAN_MONTHS.indexOf(monthName.toLowerCase());
    if (monthIndex === -1) return null;
    if (Number(day) < 1 || Number(day) > 31) return null;
    return `${year}-${pad(monthIndex + 1)}-${pad(Number(day))}`;
  }

  return null;
}
